import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';

export const login = createAsyncThunk(
  'user/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const register = createAsyncThunk(
  'user/register',
  async ({ email, password, fullName }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, full_name: fullName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// FIXED: Accept optional accessTokenParam for OAuth callback; conditional localStorage handling
export const verifyToken = createAsyncThunk(
  'user/verifyToken',
  async (accessTokenParam = null, { rejectWithValue }) => {
    try {
      let accessToken = accessTokenParam;
      const fromStorage = !accessTokenParam; // Flag: true if pulling from localStorage

      if (!accessToken) {
        accessToken = localStorage.getItem('accessToken');
      }
      if (!accessToken) {
        throw new Error('No token found');
      }

      // ADDED: Debug log (remove in prod)
      console.log("🔍 verifyToken using token:", accessToken.substring(0, 20) + '...');

      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Token verification failed');
      }

      const user = await response.json();
      // ADDED: Debug log (remove in prod)
      console.log("✅ /auth/me user:", user);

      return {
        user,
        access_token: accessToken,
        refresh_token: fromStorage ? localStorage.getItem('refreshToken') : null,
      };
    } catch (error) {
      // Only clear storage if from localStorage (not OAuth param)
      if (accessTokenParam === null) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
      // ADDED: Debug log (remove in prod)
      console.error("❌ verifyToken error:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUser = createAsyncThunk(
  'user/fetchUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().user.accessToken;
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch user');
      
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchUsers = createAsyncThunk(
  'user/searchUsers',
  async (query, { getState, rejectWithValue }) => {
    try {
      if (!query || query.length < 2) return [];

      const token = getState().user.accessToken;
      const response = await fetch(`${API_BASE}/auth/users/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Search failed');
      }

      const data = await response.json();
      // Expecting { success: true, data: [...] }
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// UPDATED: Load tokens from localStorage on initialization (camelCase consistency)
const initialState = {
  isAuthenticated: !!localStorage.getItem('accessToken'),
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { access_token, refresh_token, user } = action.payload;
      state.isAuthenticated = true;
      state.user = user;
      state.accessToken = access_token;
      state.refreshToken = refresh_token;
      localStorage.setItem('accessToken', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      } else {
        localStorage.removeItem('refreshToken');
      }
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        const { access_token, refresh_token, user } = action.payload;
        state.isAuthenticated = true;
        state.user = user;
        state.accessToken = access_token;
        state.refreshToken = refresh_token;
        localStorage.setItem('accessToken', access_token);
        if (refresh_token) {
          localStorage.setItem('refreshToken', refresh_token);
        } else {
          localStorage.removeItem('refreshToken');
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        const { access_token, refresh_token, user } = action.payload;
        state.isAuthenticated = true;
        state.user = user;
        state.accessToken = access_token;
        state.refreshToken = refresh_token;
        localStorage.setItem('accessToken', access_token);
        if (refresh_token) {
          localStorage.setItem('refreshToken', refresh_token);
        } else {
          localStorage.removeItem('refreshToken');
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
  state.user = action.payload;
})
      // Verify Token
      .addCase(verifyToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        // Only store refresh if present (e.g., not OAuth without refresh)
        if (action.payload.refresh_token) {
          localStorage.setItem('refreshToken', action.payload.refresh_token);
        }
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = action.payload;
        // Storage cleared in thunk if from localStorage
      });
  },
});

export const { setCredentials, logout, clearError } = userSlice.actions;

export default userSlice.reducer;